import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  FormLabel,
  Input,
  Label,
  Select,
  TimePicker,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import { Loader2, Save } from 'lucide-react';
import { useMissedVisitOptions } from '../../hooks/useMissedVisitOptions';
import type {
  ClinicPhysicianResponse,
  MissedVisitDataCreate,
  MissedVisitDataResponse,
  MissedVisitDataUpdate,
  MissedVisitReason,
  NotificationMethod,
} from '../../services/api/types.gen';

/**
 * Debounce window for missed-visit autosave. Shared with the AutoSavePill's
 * countdown bar (passed as its debounceMs) so the bar fills in step with the
 * save the form schedules.
 */
export const MISSED_VISIT_AUTOSAVE_DEBOUNCE_MS = 2000;

function formatPhysician(p: ClinicPhysicianResponse): string {
  return `${p.last_name}, ${p.first_name}`;
}

interface MissedVisitFormValues {
  traveledToPatient: boolean;
  attemptMadeTime: string;
  reason: MissedVisitReason;
  comments: string;
  orderGenerated: boolean;
  mdNotified: boolean;
  mdNotifiedMethod: NotificationMethod | '';
  mdNotifiedOther: string;
  mdNotifiedPhysicianId: string;
  supervisorNotified: boolean;
  supervisorNotifiedMethod: NotificationMethod | '';
  supervisorNotifiedOther: string;
}

/**
 * Canonicalizes a time to "HH:MM". The <input type="time"> widget emits
 * "HH:MM", but the backend stores a time and serializes it back as "HH:MM:SS";
 * comparing and sending a single "HH:MM" form keeps a just-saved record from
 * reading back as dirty (which, under autosave, would loop saves forever).
 */
const normalizeAttemptTime = (time: string): string => time.slice(0, 5);

/**
 * Normalizes widget state into the payload shape the save sends: comments
 * trimmed, time canonicalized, conditionally hidden fields nulled. Dirtiness
 * compares these normalized fields rather than raw widget state, so trailing
 * whitespace, a "HH:MM:SS" round-trip, or a value left in a hidden field (e.g.
 * a notification method picked before its checkbox was unchecked) doesn't read
 * as an unsaved edit after a save round-trips.
 */
const buildMissedVisitFields = (values: MissedVisitFormValues) => ({
  traveled_to_patient: values.traveledToPatient,
  attempt_made_time:
    values.traveledToPatient && values.attemptMadeTime
      ? normalizeAttemptTime(values.attemptMadeTime)
      : null,
  reason: values.reason,
  comments: values.comments.trim() || null,
  order_generated: values.orderGenerated,
  md_notified: values.mdNotified,
  md_notified_method:
    values.mdNotified && values.mdNotifiedMethod
      ? values.mdNotifiedMethod
      : null,
  md_notified_other:
    values.mdNotified && values.mdNotifiedMethod === 'other'
      ? values.mdNotifiedOther.trim() || null
      : null,
  md_notified_physician_id:
    values.mdNotified && values.mdNotifiedPhysicianId
      ? values.mdNotifiedPhysicianId
      : null,
  supervisor_notified: values.supervisorNotified,
  supervisor_notified_method:
    values.supervisorNotified && values.supervisorNotifiedMethod
      ? values.supervisorNotifiedMethod
      : null,
  supervisor_notified_other:
    values.supervisorNotified && values.supervisorNotifiedMethod === 'other'
      ? values.supervisorNotifiedOther.trim() || null
      : null,
});

type MissedVisitFormProps = {
  /** Existing missed visit data for edit mode; null/undefined for initial creation. */
  data: MissedVisitDataResponse | null | undefined;
  isReadonly: boolean;
  isSaving: boolean;
  /**
   * Autosave mode (inline editing of an existing missed visit): the form
   * debounces and calls onUpdate itself as fields change, and the manual save
   * button is hidden in favor of the parent's AutoSavePill. Off (the default)
   * keeps the explicit footer button, as in the create dialog.
   */
  autosave?: boolean;
  /**
   * In autosave mode, set when the last autosave failed. Re-shows the manual
   * save button so the user can retry a stranded save (autosave will not
   * re-attempt the same failed payload on its own).
   */
  saveErrored?: boolean;
  /** Available physicians for the MD notified physician selector. */
  physicians?: ClinicPhysicianResponse[];
  /**
   * Reports whether the normalized save payload differs from the last saved
   * data, plus whether the form is currently valid to save. The parent uses
   * `canSave` to distinguish a pending autosave (dirty + valid) from a held
   * one (dirty but incomplete), so it doesn't show a save countdown that will
   * never fire. Re-fired whenever the payload, the saved baseline, or validity
   * changes, so parents tracking an edit sequence also see edits made during
   * an in-flight save.
   */
  onDirtyChange?: (isDirty: boolean, canSave: boolean) => void;
} & (
  | {
      /** Called when creating a new missed visit record. */
      onSave: (payload: MissedVisitDataCreate) => Promise<void>;
      onUpdate?: (payload: MissedVisitDataUpdate) => Promise<void>;
    }
  | {
      onSave?: (payload: MissedVisitDataCreate) => Promise<void>;
      /** Called when updating an existing missed visit record. */
      onUpdate: (payload: MissedVisitDataUpdate) => Promise<void>;
    }
);

export const MissedVisitForm: React.FC<MissedVisitFormProps> = ({
  data,
  isReadonly,
  isSaving,
  autosave = false,
  saveErrored = false,
  onSave,
  onUpdate,
  physicians = [],
  onDirtyChange,
}) => {
  const {
    reasonOptions,
    notificationMethodOptions,
    reasonLabels,
    notificationMethodLabels,
    loading: optionsLoading,
    error: optionsError,
  } = useMissedVisitOptions();
  // The last saved values, derived with the same defaults as the field state
  // below. Dirtiness is "the normalized payload differs from these, also
  // normalized", so reverting an edit by hand returns the form to clean, and
  // a completed save (which refreshes `data`) marks the form clean without
  // remounting it.
  const savedValues = useMemo(
    () => ({
      traveledToPatient: data?.traveled_to_patient ?? false,
      attemptMadeTime: data?.attempt_made_time ?? '',
      reason: data?.reason ?? ('cancellation_of_care' as MissedVisitReason),
      comments: data?.comments ?? '',
      orderGenerated: data?.order_generated ?? false,
      mdNotified: data?.md_notified ?? false,
      mdNotifiedMethod: (data?.md_notified_method ?? '') as
        NotificationMethod | '',
      mdNotifiedOther: data?.md_notified_other ?? '',
      mdNotifiedPhysicianId: data?.md_notified_physician_id ?? '',
      supervisorNotified: data?.supervisor_notified ?? false,
      supervisorNotifiedMethod: (data?.supervisor_notified_method ?? '') as
        NotificationMethod | '',
      supervisorNotifiedOther: data?.supervisor_notified_other ?? '',
    }),
    [data]
  );
  const [traveledToPatient, setTraveledToPatient] = useState(
    savedValues.traveledToPatient
  );
  const [attemptMadeTime, setAttemptMadeTime] = useState(
    savedValues.attemptMadeTime
  );
  const [reason, setReason] = useState<MissedVisitReason>(savedValues.reason);
  const [comments, setComments] = useState(savedValues.comments);
  const [orderGenerated, setOrderGenerated] = useState(
    savedValues.orderGenerated
  );
  const [mdNotified, setMdNotified] = useState(savedValues.mdNotified);
  const [mdNotifiedMethod, setMdNotifiedMethod] = useState<
    NotificationMethod | ''
  >(savedValues.mdNotifiedMethod);
  const [mdNotifiedOther, setMdNotifiedOther] = useState(
    savedValues.mdNotifiedOther
  );
  const [mdNotifiedPhysicianId, setMdNotifiedPhysicianId] = useState(
    savedValues.mdNotifiedPhysicianId
  );
  const [supervisorNotified, setSupervisorNotified] = useState(
    savedValues.supervisorNotified
  );
  const [supervisorNotifiedMethod, setSupervisorNotifiedMethod] = useState<
    NotificationMethod | ''
  >(savedValues.supervisorNotifiedMethod);
  const [supervisorNotifiedOther, setSupervisorNotifiedOther] = useState(
    savedValues.supervisorNotifiedOther
  );

  const currentValues: typeof savedValues = {
    traveledToPatient,
    attemptMadeTime,
    reason,
    comments,
    orderGenerated,
    mdNotified,
    mdNotifiedMethod,
    mdNotifiedOther,
    mdNotifiedPhysicianId,
    supervisorNotified,
    supervisorNotifiedMethod,
    supervisorNotifiedOther,
  };
  // Compare the payload we would send against the saved record (both run
  // through the same normalization), not raw widget state. The JSON keys
  // change exactly when the normalized payload or baseline changes, so the
  // dirty report re-fires for edits made during an in-flight save without
  // enumerating every field state in a dependency list.
  const currentFields = buildMissedVisitFields(currentValues);
  const savedFields = buildMissedVisitFields(savedValues);
  const currentFieldsKey = JSON.stringify(currentFields);
  const savedFieldsKey = JSON.stringify(savedFields);
  const isDirty = currentFieldsKey !== savedFieldsKey;

  const commentsRequired = reason === 'other';
  const mdMethodRequired = mdNotified;
  const mdOtherRequired = mdNotified && mdNotifiedMethod === 'other';
  const mdPhysicianRequired = mdNotified && physicians.length > 0;
  const noPhysiciansOnFile = physicians.length === 0;
  const supervisorMethodRequired = supervisorNotified;
  const supervisorOtherRequired =
    supervisorNotified && supervisorNotifiedMethod === 'other';

  const canSave =
    // The reason dropdown is empty until the options load, and a missed visit
    // can't be saved without a reason — block until options are available.
    !optionsLoading &&
    !optionsError &&
    (!commentsRequired || comments.trim().length > 0) &&
    (!mdMethodRequired || mdNotifiedMethod !== '') &&
    (!mdOtherRequired || mdNotifiedOther.trim().length > 0) &&
    (!mdPhysicianRequired || mdNotifiedPhysicianId !== '') &&
    (!supervisorMethodRequired || supervisorNotifiedMethod !== '') &&
    (!supervisorOtherRequired || supervisorNotifiedOther.trim().length > 0);

  useEffect(() => {
    onDirtyChange?.(isDirty, canSave);
  }, [isDirty, canSave, currentFieldsKey, savedFieldsKey, onDirtyChange]);

  const handleSave = async () => {
    if (onUpdate) {
      await onUpdate(currentFields);
    } else if (onSave) {
      await onSave(currentFields);
    }
  };

  // Autosave (inline edit): debounce a save whenever the normalized payload
  // differs from what's saved and the form is valid. Held in refs so the
  // effect can resend the latest payload without listing the per-render
  // object/handler in its deps (which would reset the debounce every render).
  const currentFieldsRef = useRef(currentFields);
  currentFieldsRef.current = currentFields;
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  useEffect(() => {
    // saveErrored gates auto-retry: a failed save leaves the form dirty, and
    // without this the effect would re-fire against the failure every debounce
    // window. The user retries via the manual button (shown while errored) or
    // by editing, which clears the error and re-arms this. Correctness here
    // relies on buildMissedVisitFields being round-trip-stable (e.g. the time
    // normalization) so a saved record reads back clean rather than resaving.
    if (
      !autosave ||
      isReadonly ||
      isSaving ||
      saveErrored ||
      !isDirty ||
      !canSave
    ) {
      return;
    }
    const timer = setTimeout(() => {
      void onUpdateRef.current?.(currentFieldsRef.current);
    }, MISSED_VISIT_AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // currentFieldsKey stands in for currentFields: it changes exactly when the
    // payload does, restarting the debounce on each edit. isSaving gates a new
    // save until the in-flight one finishes, then re-runs to flush later edits.
  }, [
    autosave,
    isReadonly,
    isSaving,
    saveErrored,
    isDirty,
    canSave,
    currentFieldsKey,
  ]);

  if (isReadonly && data) {
    const mdPhysician = physicians.find(
      p => p.id === data.md_notified_physician_id
    );
    return (
      <div className='space-y-4 rounded-lg border bg-muted/20 p-5'>
        <h3 className='font-semibold text-sm text-muted-foreground uppercase tracking-wide'>
          Missed Visit Details
        </h3>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm'>
          <ReadonlyRow
            label='Traveled to Patient'
            value={data.traveled_to_patient ? 'Yes' : 'No'}
          />
          {data.traveled_to_patient && data.attempt_made_time && (
            <ReadonlyRow label='Attempt Made' value={data.attempt_made_time} />
          )}
          <ReadonlyRow
            label='Reason'
            value={reasonLabels[data.reason] ?? data.reason}
          />
          {data.comments && (
            <ReadonlyRow
              label='Comments'
              value={data.comments}
              className='sm:col-span-2'
            />
          )}
          <ReadonlyRow
            label='Order Generated'
            value={data.order_generated ? 'Yes' : 'No'}
          />
          <ReadonlyRow
            label='MD Notified'
            value={data.md_notified ? 'Yes' : 'No'}
          />
          {data.md_notified && data.md_notified_method && (
            <ReadonlyRow
              label='MD Notified Via'
              value={
                notificationMethodLabels[data.md_notified_method] ??
                data.md_notified_method
              }
            />
          )}
          {data.md_notified &&
            data.md_notified_method === 'other' &&
            data.md_notified_other && (
              <ReadonlyRow
                label='MD Notification Details'
                value={data.md_notified_other}
              />
            )}
          {data.md_notified && data.md_notified_physician_id && (
            <ReadonlyRow
              label='Physician Notified'
              value={
                mdPhysician
                  ? formatPhysician(mdPhysician)
                  : '(Physician no longer on file)'
              }
            />
          )}
          <ReadonlyRow
            label='Supervisor Notified'
            value={data.supervisor_notified ? 'Yes' : 'No'}
          />
          {data.supervisor_notified && data.supervisor_notified_method && (
            <ReadonlyRow
              label='Supervisor Notified Via'
              value={
                notificationMethodLabels[data.supervisor_notified_method] ??
                data.supervisor_notified_method
              }
            />
          )}
          {data.supervisor_notified &&
            data.supervisor_notified_method === 'other' &&
            data.supervisor_notified_other && (
              <ReadonlyRow
                label='Supervisor Notification Details'
                value={data.supervisor_notified_other}
              />
            )}
          {data.approved !== null && data.approved !== undefined && (
            <ReadonlyRow
              label='Approved'
              value={data.approved ? 'Yes' : 'No'}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-5 rounded-lg border p-5'>
      <h3 className='font-semibold'>Missed Visit Details</h3>

      {optionsError && (
        <p className='text-sm text-destructive'>
          Couldn't load the form options. Please refresh and try again.
        </p>
      )}

      {/* Reason */}
      <div className='space-y-1.5'>
        <FormLabel htmlFor='mvd-reason' required>
          Reason for Missed Visit
        </FormLabel>
        <Select
          value={reason}
          onValueChange={v => setReason(v as MissedVisitReason)}
          disabled={optionsLoading}
        >
          <SelectTrigger id='mvd-reason' selectSize='default'>
            <SelectValue placeholder='Select a reason…'>
              {optionsLoading ? (
                <span className='flex items-center gap-1.5 text-muted-foreground'>
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  Loading reasons…
                </span>
              ) : undefined}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {reasonOptions.map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Comments */}
      <div className='space-y-1.5'>
        <FormLabel htmlFor='mvd-comments' required={commentsRequired}>
          Comments{commentsRequired ? '' : ' (optional)'}
        </FormLabel>
        <Textarea
          id='mvd-comments'
          value={comments}
          onChange={e => setComments(e.target.value)}
          placeholder={
            commentsRequired
              ? 'Required when reason is "Other"'
              : 'Additional notes about this missed visit…'
          }
          className='min-h-[80px]'
        />
      </div>

      {/* Boolean checkboxes */}
      <div className='space-y-3'>
        <CheckboxRow
          id='mvd-traveled'
          checked={traveledToPatient}
          onCheckedChange={v => {
            setTraveledToPatient(v);
            if (!v) setAttemptMadeTime('');
          }}
          label='Traveled to Patient'
        />
        {traveledToPatient && (
          <div className='ml-6 space-y-1.5'>
            <FormLabel htmlFor='mvd-attempt-time'>Attempt made</FormLabel>
            <TimePicker
              id='mvd-attempt-time'
              time={attemptMadeTime || undefined}
              onTimeChange={time => setAttemptMadeTime(time ?? '')}
              timePickerSize='sm'
              className='w-36'
            />
          </div>
        )}
        <CheckboxRow
          id='mvd-order'
          checked={orderGenerated}
          onCheckedChange={setOrderGenerated}
          label='Order Generated'
        />

        {/* MD Notified */}
        <CheckboxRow
          id='mvd-md'
          checked={mdNotified}
          onCheckedChange={v => {
            setMdNotified(v);
            if (!v) {
              setMdNotifiedMethod('');
              setMdNotifiedOther('');
              setMdNotifiedPhysicianId('');
            }
          }}
          label='MD Notified'
        />
        {mdNotified && (
          <div className='ml-6 space-y-3'>
            <div className='space-y-1.5'>
              <FormLabel htmlFor='mvd-md-method' required>
                How was MD notified?
              </FormLabel>
              <Select
                value={mdNotifiedMethod}
                onValueChange={v => {
                  setMdNotifiedMethod(v as NotificationMethod);
                  if (v !== 'other') setMdNotifiedOther('');
                }}
                disabled={optionsLoading}
              >
                <SelectTrigger id='mvd-md-method' selectSize='sm'>
                  <SelectValue placeholder='Select method…' />
                </SelectTrigger>
                <SelectContent>
                  {notificationMethodOptions.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {mdNotifiedMethod === 'other' && (
              <div className='space-y-1.5'>
                <FormLabel htmlFor='mvd-md-other' required>
                  Describe notification method
                </FormLabel>
                <Input
                  id='mvd-md-other'
                  value={mdNotifiedOther}
                  onChange={e => setMdNotifiedOther(e.target.value)}
                  placeholder='Describe how MD was notified…'
                  inputSize='sm'
                />
              </div>
            )}
            <div className='space-y-1.5'>
              <FormLabel
                htmlFor='mvd-md-physician'
                required={!noPhysiciansOnFile}
              >
                Which physician was notified?
              </FormLabel>
              <Select
                value={mdNotifiedPhysicianId}
                onValueChange={setMdNotifiedPhysicianId}
                disabled={noPhysiciansOnFile}
              >
                <SelectTrigger id='mvd-md-physician' selectSize='sm'>
                  <SelectValue
                    placeholder={
                      noPhysiciansOnFile
                        ? 'No physicians on file'
                        : 'Select physician…'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {physicians.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {formatPhysician(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Supervisor Notified */}
        <CheckboxRow
          id='mvd-supervisor'
          checked={supervisorNotified}
          onCheckedChange={v => {
            setSupervisorNotified(v);
            if (!v) {
              setSupervisorNotifiedMethod('');
              setSupervisorNotifiedOther('');
            }
          }}
          label='Supervisor Notified'
        />
        {supervisorNotified && (
          <div className='ml-6 space-y-3'>
            <div className='space-y-1.5'>
              <FormLabel htmlFor='mvd-sup-method' required>
                How was supervisor notified?
              </FormLabel>
              <Select
                value={supervisorNotifiedMethod}
                onValueChange={v => {
                  setSupervisorNotifiedMethod(v as NotificationMethod);
                  if (v !== 'other') setSupervisorNotifiedOther('');
                }}
                disabled={optionsLoading}
              >
                <SelectTrigger id='mvd-sup-method' selectSize='sm'>
                  <SelectValue placeholder='Select method…' />
                </SelectTrigger>
                <SelectContent>
                  {notificationMethodOptions.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {supervisorNotifiedMethod === 'other' && (
              <div className='space-y-1.5'>
                <FormLabel htmlFor='mvd-sup-other' required>
                  Describe notification method
                </FormLabel>
                <Input
                  id='mvd-sup-other'
                  value={supervisorNotifiedOther}
                  onChange={e => setSupervisorNotifiedOther(e.target.value)}
                  placeholder='Describe how supervisor was notified…'
                  inputSize='sm'
                />
              </div>
            )}
          </div>
        )}
      </div>

      {commentsRequired && !comments.trim() && (
        <p className='text-xs text-destructive'>
          Comments are required when the reason is "Other".
        </p>
      )}

      {/* Autosave mode hides the button; the parent's AutoSavePill reports
          save status. The create dialog keeps an explicit footer action, and
          a failed autosave re-shows it so the user can retry manually. */}
      {(!autosave || saveErrored) && (
        <div className='flex justify-end border-t pt-4'>
          <Button
            onClick={handleSave}
            disabled={isSaving || !canSave}
            className='flex items-center gap-1.5'
          >
            {isSaving ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Saving…
              </>
            ) : (
              <>
                <Save className='h-4 w-4' />
                Save Missed Visit Details
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

const ReadonlyRow: React.FC<{
  label: string;
  value: string;
  className?: string;
}> = ({ label, value, className }) => (
  <div className={className}>
    <p className='text-xs text-muted-foreground'>{label}</p>
    <p className='text-sm font-medium'>{value}</p>
  </div>
);

const CheckboxRow: React.FC<{
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
}> = ({ id, checked, onCheckedChange, label }) => (
  <div className='flex items-center gap-2'>
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={v => onCheckedChange(Boolean(v))}
      checkboxSize='default'
    />
    <Label htmlFor={id} className='cursor-pointer font-normal'>
      {label}
    </Label>
  </div>
);
